/**
 * Blog Sort Controller Component
 * Implements app-free, high-performance blog post sorting (New to Old & Old to New)
 * Supports multi-page blogs via Shopify Section Rendering API with page inversion.
 */
if (!window.customElements.get('blog-sort-by')) {
  class BlogSortBy extends HTMLElement {
    constructor() {
      super();
      this.isLoading = false;
      this._onSortChanged = this._onSortChanged.bind(this);
      this._onPaginationClick = this._onPaginationClick.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.sectionId = this.getAttribute('section-id') || (this.section ? this.section.id.replace('shopify-section-', '') : '');
      this.totalPages = parseInt(this.getAttribute('total-pages'), 10) || 1;

      const urlParams = new URLSearchParams(window.location.search);
      const activeSort = urlParams.get('sort_by');
      const activePage = parseInt(urlParams.get('page'), 10) || parseInt(this.getAttribute('current-page'), 10) || 1;
      this.currentDisplayPage = activePage;

      this.addEventListener('listbox:change', this._onSortChanged);

      // Handle pagination click delegation within the section
      if (this.section) {
        this.section.addEventListener('click', this._onPaginationClick);
      }

      if (activeSort === 'date-ascending') {
        this._updateUiSelected('date-ascending');
        if (this.totalPages > 1) {
          this._loadInvertedPage(this.currentDisplayPage, false);
        } else {
          this._sortDomArticles('date-ascending');
        }
      }
    }

    disconnectedCallback() {
      this.removeEventListener('listbox:change', this._onSortChanged);
      if (this.section) {
        this.section.removeEventListener('click', this._onPaginationClick);
      }
    }

    _updateUiSelected(sortValue) {
      const valueSpan = this.querySelector('[id$="-value"]');
      const selectedOption = this.querySelector(`[value="${sortValue}"]`);

      if (selectedOption) {
        this.querySelectorAll('.popover-listbox__option').forEach(opt => opt.removeAttribute('aria-selected'));
        selectedOption.setAttribute('aria-selected', 'true');
        if (valueSpan) {
          const textEl = selectedOption.querySelector('.reversed-link') || selectedOption;
          valueSpan.textContent = textEl.textContent.trim();
        }
      }
    }

    _onSortChanged(event) {
      const newSort = event.detail.value;
      const url = new URL(window.location.href);

      if (url.searchParams.get('sort_by') === newSort) {
        return;
      }

      this._updateUiSelected(newSort);
      this.currentDisplayPage = 1;

      if (newSort === 'date-descending') {
        url.searchParams.delete('sort_by');
        url.searchParams.delete('page');
        window.history.replaceState({}, '', url.toString());

        if (this.totalPages > 1) {
          this._loadNormalPage(1);
        } else {
          this._sortDomArticles('date-descending');
        }
      } else if (newSort === 'date-ascending') {
        url.searchParams.set('sort_by', 'date-ascending');
        url.searchParams.delete('page');
        window.history.replaceState({}, '', url.toString());

        if (this.totalPages > 1) {
          this._loadInvertedPage(1);
        } else {
          this._sortDomArticles('date-ascending');
        }
      }
    }

    _onPaginationClick(event) {
      const paginationLink = event.target.closest('.pagination__item');
      if (!paginationLink || paginationLink.classList.contains('pagination__item--disabled') || !paginationLink.getAttribute('href')) {
        return;
      }

      // Prevent default browser navigation so we can handle it cleanly via AJAX
      event.preventDefault();
      event.stopPropagation();

      if (this.isLoading) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const isAscending = urlParams.get('sort_by') === 'date-ascending';

      // Extract target page robustly from data attribute, URL, or rel
      let targetPage = null;
      if (paginationLink.dataset.targetDisplayPage) {
        targetPage = parseInt(paginationLink.dataset.targetDisplayPage, 10);
      } else {
        try {
          const linkUrl = new URL(paginationLink.href, window.location.origin);
          const pageParam = linkUrl.searchParams.get('page');
          if (pageParam) {
            targetPage = parseInt(pageParam, 10);
          }
        } catch (e) {
          // ignore parsing error
        }

        if (!targetPage) {
          if (paginationLink.getAttribute('rel') === 'prev') {
            targetPage = Math.max(1, this.currentDisplayPage - 1);
          } else if (paginationLink.getAttribute('rel') === 'next') {
            targetPage = Math.min(this.totalPages, this.currentDisplayPage + 1);
          }
        }
      }

      if (!targetPage || isNaN(targetPage)) {
        return;
      }

      if (isAscending && this.totalPages > 1) {
        this._loadInvertedPage(targetPage, true);
      } else {
        this._loadNormalPage(targetPage, true);
      }
    }

    _sortDomArticles(sortOrder) {
      const grid = this.section ? this.section.querySelector('.blog-posts') : document.querySelector('.blog-posts');
      if (!grid) return;

      const newsletter = grid.querySelector('.blog-posts-newsletter');
      const standardCards = Array.from(grid.querySelectorAll('blog-post-card:not(.blog-post-card--featured)'));

      standardCards.sort((a, b) => {
        const timeA = parseInt(a.dataset.publishedAt, 10) || 0;
        const timeB = parseInt(b.dataset.publishedAt, 10) || 0;
        return sortOrder === 'date-ascending' ? timeA - timeB : timeB - timeA;
      });

      let newsletterIndex = -1;
      if (newsletter) {
        newsletterIndex = Array.from(grid.children).indexOf(newsletter);
      }

      standardCards.forEach((card, index) => {
        if (newsletter && index === newsletterIndex) {
          grid.appendChild(newsletter);
        }
        grid.appendChild(card);
      });
    }

    async _loadInvertedPage(displayPage, updateUrl = true) {
      if (!this.section || this.isLoading) return;

      this.isLoading = true;
      this.currentDisplayPage = displayPage;
      const backendPage = Math.max(1, this.totalPages - displayPage + 1);
      this.section.classList.add('is-loading');

      try {
        const requestUrl = new URL(window.location.href);
        requestUrl.searchParams.set('section_id', this.sectionId);
        requestUrl.searchParams.set('page', backendPage);

        const response = await fetch(requestUrl.toString());
        if (!response.ok) throw new Error('Failed to fetch blog section');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newPostsGrid = doc.querySelector('.blog-posts');
        const newSortComponent = doc.querySelector('blog-sort-by');

        if (newSortComponent && newSortComponent.getAttribute('total-pages')) {
          this.totalPages = parseInt(newSortComponent.getAttribute('total-pages'), 10) || this.totalPages;
        }

        if (newPostsGrid) {
          const liveGrid = this.section.querySelector('.blog-posts');
          if (liveGrid) {
            const cards = Array.from(newPostsGrid.querySelectorAll('blog-post-card:not(.blog-post-card--featured)'));
            cards.reverse();

            const newsletter = newPostsGrid.querySelector('.blog-posts-newsletter');
            liveGrid.innerHTML = '';

            cards.forEach((card) => {
              liveGrid.appendChild(card);
            });

            if (newsletter) {
              liveGrid.appendChild(newsletter);
            }
          }
        }

        this._renderInvertedPagination(displayPage);

        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', 'date-ascending');
          if (displayPage > 1) {
            url.searchParams.set('page', displayPage);
          } else {
            url.searchParams.delete('page');
          }
          window.history.replaceState({}, '', url.toString());

          const topTarget = this.section.querySelector('.blog-toolbar') || this.section;
          topTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (err) {
        console.error('Error loading inverted blog page:', err);
      } finally {
        this.isLoading = false;
        this.section.classList.remove('is-loading');
      }
    }

    async _loadNormalPage(pageNumber, updateUrl = true) {
      if (!this.section || this.isLoading) return;

      this.isLoading = true;
      this.currentDisplayPage = pageNumber;
      this.section.classList.add('is-loading');

      try {
        const requestUrl = new URL(window.location.href);
        requestUrl.searchParams.set('section_id', this.sectionId);
        requestUrl.searchParams.set('page', pageNumber);

        const response = await fetch(requestUrl.toString());
        if (!response.ok) throw new Error('Failed to fetch blog section');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newPostsGrid = doc.querySelector('.blog-posts');
        const newPagination = doc.querySelector('.pagination');
        const newSortComponent = doc.querySelector('blog-sort-by');

        if (newSortComponent && newSortComponent.getAttribute('total-pages')) {
          this.totalPages = parseInt(newSortComponent.getAttribute('total-pages'), 10) || this.totalPages;
        }

        const liveGrid = this.section.querySelector('.blog-posts');
        const livePagination = this.section.querySelector('.pagination');

        if (newPostsGrid && liveGrid) {
          liveGrid.innerHTML = newPostsGrid.innerHTML;
        }

        if (newPagination && livePagination) {
          livePagination.innerHTML = newPagination.innerHTML;
        } else if (newPagination && !livePagination) {
          const container = this.section.querySelector('.blog-posts__container');
          if (container) container.appendChild(newPagination);
        }

        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.delete('sort_by');
          if (pageNumber > 1) {
            url.searchParams.set('page', pageNumber);
          } else {
            url.searchParams.delete('page');
          }
          window.history.replaceState({}, '', url.toString());

          const topTarget = this.section.querySelector('.blog-toolbar') || this.section;
          topTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (err) {
        console.error('Error loading normal blog page:', err);
      } finally {
        this.isLoading = false;
        this.section.classList.remove('is-loading');
      }
    }

    _renderInvertedPagination(displayPage) {
      const paginationNav = this.section.querySelector('.pagination');
      if (!paginationNav) return;

      const hasPrev = displayPage > 1;
      const hasNext = displayPage < this.totalPages;
      const prevDisplayPage = displayPage - 1;
      const nextDisplayPage = displayPage + 1;

      paginationNav.innerHTML = `
        ${
          hasPrev
            ? `<a class="pagination__item group" rel="prev" aria-label="Go to page ${prevDisplayPage}" href="?sort_by=date-ascending&page=${prevDisplayPage}" data-target-display-page="${prevDisplayPage}">
                <span class="animated-arrow animated-arrow--reverse"></span>
              </a>`
            : `<span class="pagination__item pagination__item--disabled">
                <span class="animated-arrow animated-arrow--reverse"></span>
              </span>`
        }
        <span class="pagination__current">${displayPage}&nbsp;&nbsp;/&nbsp;&nbsp;${this.totalPages}</span>
        ${
          hasNext
            ? `<a class="pagination__item group" rel="next" aria-label="Go to page ${nextDisplayPage}" href="?sort_by=date-ascending&page=${nextDisplayPage}" data-target-display-page="${nextDisplayPage}">
                <span class="animated-arrow"></span>
              </a>`
            : `<span class="pagination__item pagination__item--disabled">
                <span class="animated-arrow"></span>
              </span>`
        }
      `;
    }
  }

  window.customElements.define('blog-sort-by', BlogSortBy);
}
